import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, Phone, User, LogOut, Truck, Moon, Sun, LayoutDashboard } from 'lucide-react';
import './Navbar.css';
import { ReactComponent as Logo } from '../../assets/zeal-movers-logo.svg';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { useSiteSettings, telHref } from '../../context/SiteSettingsContext';

const Navbar = () => {
  const { contactPhone } = useSiteSettings();
  const [isOpen, setIsOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, isAuthenticated } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const userMenuRef = useRef(null);

  const portalPath = user?.role === 'admin' ? '/admin' : user?.role === 'mover' ? '/mover' : '/portal';

  const isActive = (path) => location.pathname === path;

  const handleLogout = () => {
    logout();
    navigate('/');
    setIsUserMenuOpen(false);
  };

  const handleMenuItemClick = (path) => {
    navigate(path);
    setIsUserMenuOpen(false);
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setIsUserMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <nav className="navbar">
      {/* Mobile menu overlay — tap to close */}
      {isOpen && (
        <div
          className="navbar-overlay"
          onClick={() => setIsOpen(false)}
          onKeyDown={(e) => e.key === 'Escape' && setIsOpen(false)}
          role="button"
          tabIndex={0}
          aria-label="Close menu"
        />
      )}
      <div className="container">
        <div className="navbar-content">
          <Link to="/" className="navbar-brand">
            <Logo className="navbar-logo" />
          </Link>

          <div className={`navbar-menu ${isOpen ? 'active' : ''}`}>
            <div className="navbar-menu-nav">
              <Link 
                to="/" 
                className={`navbar-link ${isActive('/') ? 'active' : ''}`}
                onClick={() => setIsOpen(false)}
              >
                Home
              </Link>
              <Link 
                to="/services" 
                className={`navbar-link ${isActive('/services') ? 'active' : ''}`}
                onClick={() => setIsOpen(false)}
              >
                Services
              </Link>
              <Link 
                to="/quote" 
                className={`navbar-link ${isActive('/quote') ? 'active' : ''}`}
                onClick={() => setIsOpen(false)}
              >
                Request a move
              </Link>
              <Link 
                to="/about" 
                className={`navbar-link ${isActive('/about') ? 'active' : ''}`}
                onClick={() => setIsOpen(false)}
              >
                About
              </Link>
              <Link 
                to="/track" 
                className={`navbar-link ${isActive('/track') ? 'active' : ''}`}
                onClick={() => setIsOpen(false)}
              >
                Track Your Move
              </Link>
              <Link 
                to="/contact" 
                className={`navbar-link ${isActive('/contact') ? 'active' : ''}`}
                onClick={() => setIsOpen(false)}
              >
                Contact
              </Link>
            </div>

            {/* Mobile-only: auth & actions inside the same slide-out */}
            <div className="navbar-menu-mobile-actions">
              <div className="navbar-menu-mobile-divider" aria-hidden="true" />
              <a href={telHref(contactPhone)} className="navbar-menu-mobile-phone" onClick={() => setIsOpen(false)}>
                <Phone size={20} />
                <span>{contactPhone}</span>
              </a>
              <button
                type="button"
                className="navbar-menu-mobile-theme"
                onClick={() => { toggleTheme(); }}
                aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
              >
                {isDark ? <Sun size={20} /> : <Moon size={20} />}
                <span>{isDark ? 'Light mode' : 'Dark mode'}</span>
              </button>
              <div className="navbar-menu-mobile-divider" aria-hidden="true" />
              {isAuthenticated ? (
                <>
                  <button
                    type="button"
                    className="navbar-menu-mobile-item navbar-menu-mobile-item-primary"
                    onClick={() => { handleMenuItemClick(portalPath); setIsOpen(false); }}
                  >
                    <LayoutDashboard size={20} />
                    <span>Portal</span>
                  </button>
                  <button
                    type="button"
                    className="navbar-menu-mobile-item"
                    onClick={() => { handleLogout(); setIsOpen(false); }}
                  >
                    <LogOut size={20} />
                    <span>Logout</span>
                  </button>
                </>
              ) : (
                <>
                  <button
                    type="button"
                    className="navbar-menu-mobile-item"
                    onClick={() => { handleMenuItemClick('/login?role=mover'); setIsOpen(false); }}
                  >
                    <Truck size={20} />
                    <span>Become a Mover</span>
                  </button>
                  <div className="navbar-menu-mobile-divider" aria-hidden="true" />
                  <button
                    type="button"
                    className="navbar-menu-mobile-item navbar-menu-mobile-item-primary"
                    onClick={() => { handleMenuItemClick('/login'); setIsOpen(false); }}
                  >
                    <User size={20} />
                    <span>Login</span>
                  </button>
                </>
              )}
            </div>
          </div>

          <div className="navbar-actions">
            <button
              type="button"
              className="navbar-theme-toggle"
              onClick={toggleTheme}
              aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
              title={isDark ? 'Light mode' : 'Dark mode'}
            >
              {isDark ? <Sun size={20} /> : <Moon size={20} />}
            </button>
            <a href={telHref(contactPhone)} className="navbar-phone">
              <Phone size={18} />
              <span>{contactPhone}</span>
            </a>

            {/* Desktop only: user dropdown (hidden on mobile; auth is in slide-out) */}
            <div className="user-menu-wrapper" ref={userMenuRef}>
              <button
                className="user-menu-toggle"
                onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                aria-label="User menu"
              >
                <User size={22} />
              </button>

              {isUserMenuOpen && (
                <div className="user-menu-dropdown">
                  {isAuthenticated ? (
                    <>
                      <button
                        type="button"
                        onClick={() => handleMenuItemClick(portalPath)}
                        className="user-menu-item user-menu-item--portal"
                      >
                        <LayoutDashboard size={18} />
                        <span>Portal</span>
                      </button>
                      <div className="user-menu-divider"></div>
                      <button
                        type="button"
                        onClick={handleLogout}
                        className="user-menu-item"
                      >
                        <LogOut size={18} />
                        <span>Logout</span>
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        onClick={() => handleMenuItemClick('/login?role=mover')}
                        className="user-menu-item"
                      >
                        <Truck size={18} />
                        <span>Become a Mover</span>
                      </button>
                      <div className="user-menu-divider"></div>
                      <button
                        onClick={() => handleMenuItemClick('/login')}
                        className="user-menu-item"
                      >
                        <User size={18} />
                        <span>Login</span>
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>

          <button
            className="navbar-toggle"
            onClick={() => setIsOpen(!isOpen)}
            aria-label={isOpen ? 'Close menu' : 'Open menu'}
          >
            <Menu size={24} />
          </button>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
